import { make } from 'vuex-pathify';
import axios from "axios";
import { triggerError } from "@/components/helpers/error-handler";
import { userService } from "@/services/auth.service";

const timeout = 900000; //15 minutes

const getDefaultState = () => {
  return {
    token: localStorage.getItem("token") || "",
    verified: false,
    verifiedUpdated: null,
    user: JSON.parse(localStorage.getItem("user")) || {},
    passwordUpdated: false,
    passwordForgot: false,
    twoFactorCode: null,
    twoFactorQR: null,
    authCode: null,
    thirdPartyAccess: false,
  }
}

const state = getDefaultState();

const actions = {
  async login({ commit }, { email, password, captcha }) {
    try {
      commit('reset_login_check_state');

      const { status, data } = await axios({
        url: process.env.VUE_APP_API_URL + "api/login",
        data: { email: email, password: password, "g-recaptcha-response": captcha },
        method: "POST"
      });

      const validStates = ['success', 'require_set2fa', 'require_2fa', 'require_verification', 'redirect_third_party'];
      if (status !== 200 || !data.state || !validStates.includes(data.state)) {
        throw new Error("Unable to login");
      }

      if (data.state === 'success') {
        const { token, ...user } = data;
        userService.login(token, user);

        commit('login_now', { token, user });
        commit('verified');

        return {
          state: "success"
        };
      }

      const { auth_code, code, state, qr, third_party } = data;

      commit('login_check_state', { auth_code, qr, code });

      return {
        state: state,
        third_party: third_party
      };
    } catch (error) {
      userService.logout();
      triggerError(error);

      return {
        state: "fail"
      };
    }
  },
  async logout({ commit, dispatch }) {
    userService.logout();
    commit("logout");
    dispatch('app/init', null, {root: true})

    return true;
  },
  async verify({ state, commit }) {
    try {
      const time = new Date().getTime();

      if (state.verifiedUpdated && time - state.verifiedUpdated < timeout) {
        return true;
      }

      const response = await axios({
        url: process.env.VUE_APP_API_URL + "api/verify"
      });

      commit('verified', { shouldNotify: response.data.shouldNotify });
      commit('update_should_notify', { should_notify: response.data.shouldNotify });
      return true;
    } catch (error) {
      userService.logout();
      return triggerError(error);
    }
  },
  async passwordForgot({ commit }, { email, captcha }) {
    try {
      await axios({
        url: process.env.VUE_APP_API_URL + "api/password-forgot",
        data: { email: email, "g-recaptcha-response": captcha },
        method: "POST"
      });

      commit('password_forgot_trigger');

      return true;
    } catch (error) {
      return triggerError(error);
    }
  },
  async recoveryCodeReset({ commit }, { pass }) {
    try {
      const { status, data } = await axios({
        url: process.env.VUE_APP_API_URL + "api/regenerate-codes",
        data: { pass: pass },
        method: "POST"
      });

      if (status !== 200 || !data.recovery_codes) {
        throw new Error("Unable to reset codes");
      }

      commit('recovery_codes_reset');

      return {
        success: true,
        codes: data.recovery_codes
      };
    } catch (error) {
      triggerError(error);
      return {
        success: false
      }
    }
  },
  async requestThirdParty({ commit }) {
    try {
      const { status, data } = await axios({
        url: process.env.VUE_APP_API_URL + "api/request-third-party",
        method: "GET"
      });

      if (status !== 200 || !data.otp || !data.account) {
        throw new Error("Unable to access Utility Click");
      }

      commit('access_third_party');

      return {
        otp: data.otp,
        account: data.account
      };
    } catch (error) {
      triggerError(error);
      return {
        success: false
      }
    }
  },
  async passwordUpdateApp({ commit }, { pass, pass_confirmation, old_pass }) {
    try {
      await axios({
        url: process.env.VUE_APP_API_URL + "api/password-update-app",
        data: { pass: pass, pass_confirmation: pass_confirmation, old_pass: old_pass },
        method: "POST"
      });

      commit('password_update_trigger');

      return true;
    } catch (error) {
      return triggerError(error);
    }
  },
  async updateShouldNotify({ commit }, { should_notify }) {
    try {
      await axios({
        url: process.env.VUE_APP_API_URL + "api/should-notify",
        data: { should_notify: should_notify },
        method: "POST"
      });

      commit('update_should_notify', { should_notify });

      return true;
    } catch (error) {
      return triggerError(error);
    }
  },
  async passwordUpdate({ commit }, { key, pass, passConfirmation, captcha }) {
    try {
      await axios({
        url: process.env.VUE_APP_API_URL + "api/password-update",
        data: { key: key, pass: pass, pass_confirmation: passConfirmation, "g-recaptcha-response": captcha },
        method: "POST"
      });

      commit('password_update_trigger');

      return true;
    } catch (error) {
      userService.logout();
      return triggerError(error);
    }
  },
  async twoFactorSet({ commit }, { code, captcha }) {
    try {

      const { data } = await axios({
        url: process.env.VUE_APP_API_URL + "api/set-two-factor",
        data: { code: code, auth_code: state.authCode, "g-recaptcha-response": captcha },
        method: "POST"
      });

      commit('reset_login_check_state');

      return {
        success: true,
        codes: data.codes
      }
    } catch (error) {
      triggerError(error);

      return {
        success: false
      }
    }
  },
  async twoFactorVerify({ state, commit }, { code, recoveryCode, captcha }) {
    try {

      const { status, data } = await axios({
        url: process.env.VUE_APP_API_URL + "api/verify-two-factor",
        data: { code: code, auth_code: state.authCode, recoveryCode: recoveryCode, "g-recaptcha-response": captcha },
        method: "POST"
      });

      commit('reset_login_check_state');

      const allowedStates = ['success', 'redirect_third_party'];
      if (status !== 200 || !data.state || !allowedStates.includes(data.state)) {
        throw new Error("Unable to login");
      }
      const respState = data.state;
      const respThirdParty = data.third_party;

      if (respState === 'redirect_third_party') {
        return {
          success: true,
          state: respState,
          third_party: respThirdParty
        };
      }

      const { token, ...user } = data;

      userService.login(token, user);

      commit('login_now', { token, user });
      commit('verified');

      return {
        success: true,
        state: respState
      };
    } catch (error) {
      return triggerError(error);
    }
  },
  async verificationResend({ commit }, { captcha }) {
    try {
      await axios({
        url: process.env.VUE_APP_API_URL + "api/validation-resend",
        data: { auth_code: state.authCode, "g-recaptcha-response": captcha },
        method: "POST"
      });

      commit('reset_login_check_state');

      return true;
    } catch (error) {
      return triggerError(error);
    }
  },
  async verificationConfirm({ commit }, { key, captcha }) {
    try {
      await axios({
        url: process.env.VUE_APP_API_URL + "api/validation-confirm",
        data: { verify_key: key, "g-recaptcha-response": captcha },
        method: "POST"
      });

      commit('reset_login_check_state');

      return true;
    } catch (error) {
      return triggerError(error);
    }
  },
  resetAuthState ({ commit }) {
    commit('resetState')
  },
};

const mutations = {
  ...make.mutations(state),
  login_now(state, { token, user }) {
    state.isLoggedIn = true;
    state.token = token;
    state.user = user;
  },
  reset_login_check_state(state) {
    state.twoFactorCode = null;
    state.twoFactorQR = null;
    state.authCode = null;
  },
  login_check_state(state, { auth_code, qr, code }) {
    state.twoFactorCode = code;
    state.twoFactorQR = qr;
    state.authCode = auth_code;
  },
  access_third_party(state) {
    state.thirdPartyAccess = true;
  },
  verified(state) {
    state.verified = true;
    state.verifiedUpdated = new Date().getTime();
  },
  logout(state) {
    state.user = {};
    state.token = "";
    state.verified = false;
  },
  password_forgot_trigger(state) {
    state.passwordForgot = true;
  },
  password_update_trigger(state) {
    state.passwordUpdated = true;
  },
  update_should_notify(state, { should_notify }) {
    state.user.shouldNotify = should_notify;
  },
  recovery_codes_reset(state) {
    state.user.remainingCodes = 8;
  },
  resetState (state) {
    Object.assign(state, getDefaultState())
  }
};

const getters = {
  ...make.getters(state),
  isLoggedIn: state => !!state.token,
  isClientBilling: state => {
    if (typeof(state.user.acl) === 'undefined') {
      return false;
    }
    return state.user.acl.creditBilling;
  }
};

export const auth = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
